import { t } from 'i18next';
import { sumBy } from 'lodash-es';

import { BillingLineGroup } from '~hooks/useBillingLines/models';
import { JobInvoicing } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { toDecimal } from '~utils/decimal';

import { BILLING_LINE_SIDES } from './chargeSelection';

export enum ChargesGroupBy {
  CUSTOMER = 'customer',
  NONE = 'none',
  PAYEE = 'payee',
  PROJECT = 'project',
}

export const CHARGES_GROUP_BY_OPTIONS = [
  ChargesGroupBy.NONE,
  ChargesGroupBy.CUSTOMER,
  ChargesGroupBy.PAYEE,
  ChargesGroupBy.PROJECT,
];

export interface ChargesGroupOrder {
  jobs: JobInvoicing[];
  order: Order;
}

export interface ChargesGroup {
  isUnassigned: boolean;
  jobIds: string[];
  key: string;
  name: string;
  orders: ChargesGroupOrder[];
  subtitle: string | null;
}

interface GroupIdentity {
  isUnassigned: boolean;
  key: string;
  name: string;
  subtitle: string | null;
}

const unassignedGroup = (reason: string) => ({
  isUnassigned: true,
  key: `none:${reason}`,
  name: t(`approvals.approve_charges.groups.${reason}`),
  subtitle: null,
});

type GroupedBy = Exclude<ChargesGroupBy, ChargesGroupBy.NONE>;

const groupIdentityByGroupBy = {
  [ChargesGroupBy.CUSTOMER]: (job: JobInvoicing) => {
    const customer = job.order?.account;

    return customer
      ? {
          isUnassigned: false,
          key: `customer:${customer.id}`,
          name: customer.name,
          subtitle: null,
        }
      : unassignedGroup('no_customer');
  },
  [ChargesGroupBy.PAYEE]: (job: JobInvoicing) => {
    const vendor = job.vendorJobAssignment?.vendorAccount;

    if (vendor) {
      return {
        isUnassigned: false,
        key: `vendor:${vendor.id}`,
        name: vendor.name,
        subtitle: null,
      };
    }

    return job.driver
      ? {
          isUnassigned: false,
          key: `driver:${job.driver.id}`,
          name: job.driver.fullName,
          subtitle: t('approvals.approve_charges.groups.internal_driver'),
        }
      : unassignedGroup('no_payee');
  },
  [ChargesGroupBy.PROJECT]: (job: JobInvoicing) =>
    job.project
      ? {
          isUnassigned: false,
          key: `project:${job.project.id}`,
          name: job.project.name,
          subtitle: job.order?.account?.name ?? null,
        }
      : unassignedGroup('no_project'),
} satisfies Record<GroupedBy, (job: JobInvoicing) => GroupIdentity>;

export function groupChargesJobs(
  ordersWithJobs: ChargesGroupOrder[],
  groupBy: GroupedBy,
) {
  const groupsByKey = new Map<string, ChargesGroup>();

  ordersWithJobs.forEach(({ jobs, order }) => {
    jobs.forEach((job) => {
      const identity = groupIdentityByGroupBy[groupBy](job);
      const group = groupsByKey.get(identity.key) ?? {
        ...identity,
        jobIds: [],
        orders: [],
      };
      const groupOrder = group.orders.find((entry) => entry.order.id === order.id);

      if (groupOrder) {
        groupOrder.jobs.push(job);
      } else {
        group.orders.push({ jobs: [job], order });
      }

      group.jobIds.push(job.id);
      groupsByKey.set(identity.key, group);
    });
  });

  return [...groupsByKey.values()].sort((first, second) => {
    if (first.isUnassigned !== second.isUnassigned) {
      return first.isUnassigned ? 1 : -1;
    }

    return first.name.localeCompare(second.name);
  });
}

export const sumLineGroupsBySide = (lineGroups: BillingLineGroup[]) =>
  BILLING_LINE_SIDES.map((side) => {
    const sideLineGroups = lineGroups.filter((lineGroup) => lineGroup.side === side);

    return {
      eligibleCount: sumBy(sideLineGroups, (lineGroup) => lineGroup.eligibleCount),
      lineCount: sumBy(sideLineGroups, (lineGroup) => lineGroup.lineCount),
      side,
      total: sideLineGroups
        .reduce(
          (sum, lineGroup) => sum.plus(toDecimal(lineGroup.totalAmount)),
          toDecimal(0),
        )
        .toNumber(),
    };
  });
